import { useRef, useState } from "react";
import { ResponseContainer } from "store";
import { chunk } from "utils/helper";
import WebRender from "../../classes/WebRender";

const STUFFING_CHUNK = 20;
const THUMB_SIZE = { width: 280, height: 200 };

export type Images = {
  [key: string]: string;
};

type StuffingResultData = {
  result: ResponseContainer[];
};

const buildChunks = (data?: StuffingResultData | null): ResponseContainer[][] => {
  if (!data?.result?.length) return [];
  return chunk(data.result, STUFFING_CHUNK);
};

const yieldToMain = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

const useStuffingHook = (initialData?: StuffingResultData | null) => {
  const [stuffing] = useState<ResponseContainer[][]>(() => buildChunks(initialData));
  const [loadPages, setLoadPages] = useState<boolean[]>(() => {
    const list = buildChunks(initialData);
    return new Array(list.length).fill(false);
  });
  const [loadMore, setLoadMore] = useState<boolean>(true);
  const loadingPagesRef = useRef<Set<number>>(new Set());

  const setStuffing = (_data: StuffingResultData) => {
    // Prefetch path hydrates chunks via initial state; keep API for compatibility.
  };

  const loadImages = async (page: number, onProgress?: (images: Images) => void): Promise<Images> => {
    const images: Images = {};
    const list = stuffing[page - 1];
    if (!list || loadingPagesRef.current.has(page)) return images;

    loadingPagesRef.current.add(page);

    // Let the results list paint before heavy WebGL thumbnail work.
    await yieldToMain();

    for (const item of list) {
      const renderer = new WebRender(item, THUMB_SIZE);
      renderer.setCollectStoryboard(false);
      images[item.uid] = renderer.getThumbnail();
      onProgress?.({ ...images });
      await yieldToMain();
    }

    setLoadPages((prev) => {
      const pages = [...prev];
      pages[page - 1] = true;
      setLoadMore(pages.some((state) => !state));
      return pages;
    });

    return images;
  };

  const loadAllPages = async () => {
    let images: Images = {};

    for (let index = 0; index < loadPages.length; index++) {
      if (loadPages[index]) continue;
      const result = await loadImages(index + 1);
      images = { ...images, ...result };
    }

    return images;
  };

  return { loadImages, setStuffing, loadAllPages, loadMore };
};

export { useStuffingHook, STUFFING_CHUNK };
